import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable, { Column } from './DataTable';
import EmptyState from './EmptyState';
import Icon from './Icon';
import LoadErrorBox from './ui/LoadErrorBox';
import StatusBadge from './ui/StatusBadge';
import { RecordIdLabel, idSortKey } from './ui/idColumn';
import NewBookingModal from './vendorBookings/NewBookingModal';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import { BOOKINGS_NEW_EVENT, setVendorBookingsBar, useVendorBookingsBar } from '../hooks/useVendorBookingsBar';
import { useLoad } from '../hooks/useLoad';
import { useMoney } from '../hooks/useMoney';
import { usePageRefresh } from '../hooks/usePageRefresh';
import { useRole } from '../hooks/useRole';
import { fetchBookings } from '../services/VendorBookingRecordService';
import { isApiError } from '../utils/apiErrors';
import { formatDecimal } from '../utils/decimal';
import { entityPath } from '../utils/entityRoutes';
import { formatDate } from '../utils/formatDate';
import { recordLabel } from '../utils/recordId';
import { BOOKING_STATUS_LABELS, BOOKING_STATUS_OPTIONS, BOOKING_STATUS_VARIANT, statusOf } from '../utils/vendorBookingLabels';
import type { BookingRow } from '../types/VendorBookingRecord';
import '../styles/components/VendorBookings.css';

/** Whether a booking matches the header search: its number, vendor, event, status, confirmation reference or quote number. */
export const bookingMatches = (b: BookingRow, term: string): boolean => {
  if (!term) return true;
  return [
    recordLabel('vendorBooking', b.id), b.quote_id ? recordLabel('quote', b.quote_id) : null, b.vendor_name, b.event_name,
    BOOKING_STATUS_LABELS[statusOf(b.status)], b.confirmation_ref, b.contact_notes,
  ].some(v => (v ?? '').toLowerCase().includes(term));
};

const VendorBookingsList: React.FC = () => {
  const navigate = useNavigate();
  const money = useMoney();
  const { canEdit, canViewFinancials } = useRole();
  const search = useHeaderSearch().trim().toLowerCase();
  // Filters, the Archived switch and New booking live in the Header's bar (VendorBookingsListBar); this list reads the same store.
  const { archived, status, vendor } = useVendorBookingsBar();
  const [picking, setPicking] = useState(false);

  const load = useLoad(() => fetchBookings({ view: archived ? 'archived' : 'active' }), [archived]);
  usePageRefresh(() => load.reload(), load.loading);

  const rows = useMemo(() => load.data ?? [], [load.data]);
  const vendorOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const b of rows) if (!seen.has(b.vendor_id)) seen.set(b.vendor_id, b.vendor_name || 'Vendor');
    return [...seen].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);
  useEffect(() => { setVendorBookingsBar({ vendorOptions }); }, [vendorOptions]);
  useEffect(() => {
    const open = () => setPicking(true);
    window.addEventListener(BOOKINGS_NEW_EVENT, open);
    return () => window.removeEventListener(BOOKINGS_NEW_EVENT, open);
  }, []);
  const shown = useMemo(
    () => rows.filter(b => (!status || statusOf(b.status) === status) && (!vendor || b.vendor_id === vendor) && bookingMatches(b, search)),
    [rows, status, vendor, search],
  );
  // A quote with a live booking cannot start another.
  const takenQuoteIds = useMemo(
    () => new Set(rows.filter(b => !b.archived && statusOf(b.status) !== 'cancelled' && b.quote_id).map(b => b.quote_id as string)),
    [rows],
  );

  if (load.error && isApiError(load.error) && load.error.status === 403) {
    return (
      <>
        <div className="wt-container fade-in">
          <EmptyState icon="lock" title="You do not have access to vendor bookings" description="Ask the project owner to change your role if you need them." />
        </div>
      </>
    );
  }
  if (load.error) {
    return (
      <>
        <div className="wt-container fade-in"><LoadErrorBox what="vendor bookings" error={load.error} onRetry={load.reload} retrying={load.loading} /></div>
      </>
    );
  }

  const open = (b: BookingRow) => entityPath('vendorBooking', b.id) ?? '/vendor-bookings';
  const columns: Column<BookingRow>[] = [
    {
      key: 'id', header: 'ID', width: '112px', sortable: true, className: 'rec-id-cell',
      sortValue: b => idSortKey('vendorBooking', b.id),
      render: (_v, b) => <Link className="vb-row-link" to={open(b)} onClick={e => e.stopPropagation()}><RecordIdLabel kind="vendorBooking" id={b.id} /></Link>,
    },
    {
      key: 'vendor_name', header: 'Vendor', sortable: true,
      render: (_v, b) => <Link className="vb-link" to={open(b)} onClick={e => e.stopPropagation()}>{b.vendor_name || 'Vendor'}</Link>,
    },
    {
      key: 'status', header: 'Status', sortable: true, sortValue: b => statusOf(b.status),
      render: (_v, b) => <StatusBadge variant={BOOKING_STATUS_VARIANT[statusOf(b.status)]}>{BOOKING_STATUS_LABELS[statusOf(b.status)]}</StatusBadge>,
    },
    { key: 'contract_signed', header: 'Contract', sortable: true, sortValue: b => (b.contract_signed ? 1 : 0), render: (_v, b) => (b.contract_signed ? 'Signed' : 'Not signed') },
    { key: 'event_name', header: 'Event', sortable: true, render: (_v, b) => b.event_name || '—' },
    { key: 'booking_date', header: 'Booking date', sortable: true, render: (_v, b) => (b.booking_date ? formatDate(b.booking_date, '') || b.booking_date : '—') },
    {
      key: 'quote_id', header: 'Quote', sortable: true, sortValue: b => (b.quote_id ? idSortKey('quote', b.quote_id) : ''),
      render: (_v, b) => (b.quote_id
        ? <Link className="vb-link" to={entityPath('quote', b.quote_id) ?? '/quotes'} onClick={e => e.stopPropagation()}><RecordIdLabel kind="quote" id={b.quote_id} /></Link>
        : '—'),
    },
    // Money columns are left out entirely for someone who may not see money.
    ...(canViewFinancials ? [
      {
        key: 'total_amount', header: 'Total', sortable: true, align: 'right' as const, sortValue: (b: BookingRow) => Number(b.total_amount ?? 0),
        render: (_v: unknown, b: BookingRow) => formatDecimal(b.total_amount, b.currency || money.currency),
      },
      {
        key: 'total_paid', header: 'Paid', sortable: true, align: 'right' as const, sortValue: (b: BookingRow) => Number(b.total_paid ?? 0),
        render: (_v: unknown, b: BookingRow) => formatDecimal(b.total_paid, b.currency || money.currency),
      },
    ] : []),
  ];

  const filtered = !!status || !!vendor || !!search;
  const emptyTitle = rows.length > 0 || filtered
    ? 'No bookings match these filters'
    : archived ? 'No archived bookings' : 'No bookings yet. Create one from an accepted quote.';

  return (
    <>
      <div className="wt-container fade-in">
        {!load.loading && shown.length === 0 ? (
          <EmptyState
            icon="document"
            title={emptyTitle}
            action={canEdit && rows.length === 0 && !archived
              ? <button type="button" className="primary-btn" onClick={() => setPicking(true)}>New booking</button>
              : undefined}
          />
        ) : (
          <DataTable<BookingRow>
            recordList={{ kind: 'vendorBooking' }}
            data={shown}
            columns={columns}
            loading={load.loading}
            emptyMessage="No bookings"
            pagination={{ enabled: true, pageSize: 25 }}
            onRowClick={b => navigate(open(b))}
          />
        )}
      </div>
      {picking && <NewBookingModal takenQuoteIds={takenQuoteIds} onClose={() => setPicking(false)} />}
    </>
  );
};

export default VendorBookingsList;
